"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _Box = _interopRequireDefault(require("@mui/material/Box"));
var _Card = _interopRequireDefault(require("@mui/material/Card"));
var _CardActionArea = _interopRequireDefault(require("@mui/material/CardActionArea"));
var _CardActions = _interopRequireDefault(require("@mui/material/CardActions"));
var _CardContent = _interopRequireDefault(require("@mui/material/CardContent"));
var _CardMedia = _interopRequireDefault(require("@mui/material/CardMedia"));
var _styles = require("@mui/material/styles");
var _styles2 = require("@mui/styles");
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireWildcard(require("react"));
var _Flex = _interopRequireDefault(require("./Flex.js"));
var _Loadable = _interopRequireDefault(require("./Loadable.js"));
var _String = _interopRequireDefault(require("./String.js"));
var _Navigator = _interopRequireDefault(require("../App/Navigator.js"));
var _CardModule = _interopRequireDefault(require("./Card.module.scss"));
var _jsxRuntime = require("react/jsx-runtime");
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
/**
 * Abstraction of Material UI's `Card`
 */const Card = props => {
  const hasActionArea = !!(props.onClick || props.uri);
  const hasActions = !!props.actions;
  const hasMedia = !!(props.media || props.mediaUri);
  const [mediaError, setMediaError] = (0, _react.useState)(false);

  // Create a mini theme if we have an accent colour
  let theme = (0, _styles2.useTheme)();
  if (props.accentColour) {
    try {
      const componentTheme = (0, _styles.createTheme)({
        ...theme,
        palette: {
          primary: {
            main: props.accentColour ?? "#fff"
          }
        }
      });
      theme = componentTheme;
    } catch (e) {
      // ... Might not be a valid colour.
    }
  }

  /**
   * Clicked.
   *
   * @return {void}
   */
  const handleClick = () => {
    if (props.uri) {
      if (props.uri.includes("://")) {
        window.open(props.uri, "_target=blank");
      } else _Navigator.default.navigate(props.uri);
    } else {
      props.onClick?.(props.value, props.name);
    }
  };

  /**
   * The little toolbar at the bottom of a Card
   */
  const renderActions = () => {
    return /*#__PURE__*/(0, _jsxRuntime.jsx)(_CardActions.default, {
      children: props.actions
    });
  };

  /**
   * Render the clickable action area.
   *
   * @return {ReactNode}
   */
  const renderActionArea = content => {
    const sx = {
      alignItems: props.horizontal ? "center" : undefined,
      display: props.horizontal ? "flex" : undefined,
      padding: 0
    };
    if (hasActionArea) {
      return /*#__PURE__*/(0, _jsxRuntime.jsx)(_CardActionArea.default, {
        className: `hopsActionAreaMui ${_CardModule.default.actionArea} ${props.classNameActionArea || ""}`.trim(),
        disabled: props.disabled,
        onClick: handleClick,
        sx: sx,
        children: content
      });
    } else {
      return /*#__PURE__*/(0, _jsxRuntime.jsx)(_Box.default, {
        sx: sx,
        children: content
      });
    }
  };

  /**
   * Render the card content.
   * 
   * @return {ReactNode}
   */
  const renderContent = contentProps => {
    return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_jsxRuntime.Fragment, {
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_CardContent.default, {
        ...contentProps,
        className: `${_CardModule.default.cardContent} ${props.classNameContentArea || ""}`.trim(),
        children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Flex.default, {
          alignItems: props.alignItems,
          className: `${_CardModule.default.cardContentFlex} ${props.classNameFlex || ""}`.trim(),
          gap: props.gap,
          px: props.px,
          py: props.py,
          ...props.FlexProps,
          children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Loadable.default, {
            loading: props.loading,
            children: /*#__PURE__*/(0, _jsxRuntime.jsxs)(_Flex.default, {
              alignItems: props.alignItems,
              gap: 0.5,
              children: [props.title && /*#__PURE__*/(0, _jsxRuntime.jsx)(_String.default, {
                classNameTypography: _CardModule.default.TitleTextLabel,
                str: props.title,
                variant: "h6"
              }), props.subtitle && /*#__PURE__*/(0, _jsxRuntime.jsx)(_String.default, {
                classNameTypography: _CardModule.default.SubtitleTextLabel,
                str: props.subtitle,
                variant: "subtitle1"
              }), props.children]
            })
          })
        })
      }),
      // These are the buttons/toolbar, NOT the clickable action
      hasActions && renderActions()]
    });
  };

  /**
   * Render the containers for horizontal/vertical layout.
   *
   * @return {ReactNode}
   */
  const renderOrientation = () => {
    if (props.horizontal) {
      return /*#__PURE__*/(0, _jsxRuntime.jsx)(_Box.default, {
        sx: {
          alignItems: "center",
          display: "flex",
          flexDirection: "column",
          padding: 0,
          width: "100%",
          height: props.mediaSize
        },
        children: renderContent({
          sx: {
            alignItems: "center",
            alignContent: "center",
            flex: "1 0 auto",
            width: "100%"
          }
        })
      });
    } else {
      return renderContent();
    }
  };

  /**
   * Render the media.
   *
   * @return {ReactNode}
   */
  const renderMedia = () => {
    const isHorizontalLayout = props.horizontal;
    const isMediaImage = !props.media && props.mediaUri;
    const isMediaElement = props.media && !props.mediaUri;
    if (!mediaError) {
      return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_CardMedia.default, {
        sx: {
          background: isMediaElement ? theme.palette.primary.main : undefined,
          color: isMediaElement ? theme.palette.primary.contrastText : undefined,
          // Horizontal
          height: isHorizontalLayout ? props.mediaSize : undefined,
          width: isHorizontalLayout ? props.mediaSize : undefined,
          // Img Element
          objectFit: isMediaImage ? "cover" : undefined,
          // Media Element
          display: isMediaElement ? "flex" : undefined,
          alignItems: isMediaElement ? "center" : undefined,
          justifyContent: isMediaElement ? "center" : undefined
        },
        image: props.mediaUri,
        title: props.mediaTitle,
        children: [(
        /**
         * Render a hidden image, just to get the handleMediaError event
         * CardMedia does have this built in for component="img", but we
         * render all media as a div for layout consistency.
         */
        props.mediaUri && /*#__PURE__*/(0, _jsxRuntime.jsx)("img", {
          alt: "product",
          src: props.mediaUri,
          onError: handleMediaError,
          style: {
            display: "none"
          }
        })), props.media]
      });
    } else {
      return null;
    }
  };
  const handleMediaError = () => {
    setMediaError(true);
  };
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_styles.ThemeProvider, {
    theme: theme,
    children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Card.default, {
      className: `hopsCardMui ${props.className}`,
      elevation: props.outlined ? undefined : props.elevation,
      raised: props.raised,
      square: props.square,
      sx: {
        color: props.accentColour ? theme.palette.primary.contrastText : undefined,
        background: props.accentColour ? (0, _styles.alpha)(theme.palette.primary.main, 0.6) : undefined,
        minHeight: props.mediaSize
      },
      variant: props.outlined ? "outlined" : undefined,
      children: renderActionArea(/*#__PURE__*/(0, _jsxRuntime.jsxs)(_jsxRuntime.Fragment, {
        children: [hasMedia && renderMedia(), renderOrientation()]
      }))
    })
  });
};
Card.propTypes = {
  alignItems: _propTypes.default.string,
  FlexProps: _propTypes.default.object,
  children: _propTypes.default.node,
  className: _propTypes.default.string,
  classNameActionArea: _propTypes.default.string,
  classNameContentArea: _propTypes.default.string,
  classNameFlex: _propTypes.default.string,
  disabled: _propTypes.default.bool,
  elevation: _propTypes.default.number,
  horizontal: _propTypes.default.bool,
  gap: _propTypes.default.number,
  name: _propTypes.default.string,
  onClick: _propTypes.default.func,
  outlined: _propTypes.default.bool,
  media: _propTypes.default.object,
  mediaUri: _propTypes.default.string,
  mediaSize: _propTypes.default.string,
  mediaTitle: _propTypes.default.string,
  px: _propTypes.default.number,
  py: _propTypes.default.number,
  raised: _propTypes.default.bool,
  square: _propTypes.default.bool,
  title: _propTypes.default.string,
  subtitle: _propTypes.default.string,
  uri: _propTypes.default.string,
  value: _propTypes.default.any
};
Card.defaultProps = {
  alignItems: "center",
  disabled: false,
  elevation: 1,
  gap: 1,
  outlined: false,
  mediaSize: "6.5em",
  px: 1,
  py: 1,
  raised: false,
  square: false
};
var _default = exports.default = Card;